<template>
  <div class="profile-page" :class="{ 'profile-page--logging-out': loggingOut }">
    <div class="profile-page__inner">
      <button
        type="button"
        class="profile-header"
        :disabled="loggingOut"
        aria-label="进入个人详情"
        @click="goProfileInfo"
      >
        <div class="profile-header__main">
          <div class="profile-header__avatar" aria-hidden="true">
            <img v-if="avatarImageUrl" :src="avatarImageUrl" alt="" class="profile-header__avatar-img" />
            <i v-else class="el-icon-user-solid"></i>
          </div>
          <div class="profile-header__info">
            <h1 class="profile-header__name">{{ displayUserName || '—' }}</h1>
            <p class="profile-header__tagline">{{ profileTagline }}</p>
          </div>
        </div>
        <span class="profile-header__guide" aria-hidden="true">
          <span class="profile-header__guide-text">主页</span>
          <i class="el-icon-arrow-right"></i>
        </span>
      </button>

      <button
        type="button"
        class="profile-summary-card"
        :disabled="loggingOut"
        :style="{ backgroundImage: `url(${summaryCardBg})` }"
        @click="goWeekReport"
      >
        <span class="profile-summary-card__body">
          <span class="profile-summary-card__slogan">回望这一周，听见收藏留下的回声</span>
          <span class="profile-summary-card__action">
            查看个人总结
            <i class="el-icon-arrow-right" aria-hidden="true"></i>
          </span>
        </span>
      </button>

      <section class="profile-menu-card">
        <button
          v-for="(item, index) in menuItems"
          :key="item.key"
          type="button"
          class="profile-menu-item"
          :class="{ 'profile-menu-item--last': index === menuItems.length - 1 }"
          :disabled="loggingOut"
          @click="onProfileAction(item.action)"
        >
          <span class="profile-menu-item__icon" :class="`profile-menu-item__icon--${item.tone}`">
            <i :class="item.icon"></i>
          </span>
          <span class="profile-menu-item__label">{{ item.label }}</span>
          <i class="el-icon-arrow-right profile-menu-item__chevron" aria-hidden="true"></i>
        </button>
      </section>

      <button
        type="button"
        class="profile-logout-btn"
        :disabled="loggingOut"
        @click="handleLogout"
      >
        <i v-if="loggingOut" class="el-icon-loading profile-logout-btn__loading" aria-hidden="true"></i>
        <i v-else class="el-icon-s-release" aria-hidden="true"></i>
        <span>{{ loggingOut ? '正在注销…' : '退出登录' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import defaultManImg from '@/assets/default-man.jpg?url'
import defaultWomanImg from '@/assets/default-woman.jpg?url'
import summaryCardBg from '@/assets/profile-summary-bg.jpg?url'
import { resolveResourceUrl } from '@/utils/resourceUrl'
import {
  logoutWithServer,
  getStoredUserProfile,
  getProfileAvatarPreset
} from '@/utils/userSession'

export default {
  name: 'ProfilePage',
  data() {
    return {
      defaultManImg,
      defaultWomanImg,
      summaryCardBg,
      profile: getStoredUserProfile(),
      avatarPreset: getProfileAvatarPreset(),
      loggingOut: false
    }
  },
  computed: {
    avatarImageUrl() {
      const serverAvatar = this.profile && this.profile.avatar ? String(this.profile.avatar).trim() : ''
      if (serverAvatar === '/preset-avatar/man') return this.defaultManImg
      if (serverAvatar === '/preset-avatar/woman') return this.defaultWomanImg
      if (serverAvatar) return resolveResourceUrl(serverAvatar)
      if (this.avatarPreset === 'man') return this.defaultManImg
      if (this.avatarPreset === 'woman') return this.defaultWomanImg
      return ''
    },
    displayUserName() {
      const n = this.profile && this.profile.userName
      return (n && String(n).trim()) || ''
    },
    profileTagline() {
      return '让收藏，用得上'
    },
    menuItems() {
      return [
        {
          key: 'tasks',
          label: '分析任务',
          icon: 'el-icon-s-order',
          tone: 'indigo',
          action: 'goTaskList'
        },
        {
          key: 'password',
          label: '修改密码',
          icon: 'el-icon-s-tools',
          tone: 'orange',
          action: 'goChangePassword'
        },
        {
          key: 'agreement',
          label: '用户协议',
          icon: 'el-icon-s-order',
          tone: 'purple',
          action: 'goUserAgreement'
        },
        {
          key: 'privacy',
          label: '隐私政策',
          icon: 'el-icon-s-platform',
          tone: 'cyan',
          action: 'goPrivacyPolicy'
        }
      ]
    }
  },
  mounted() {
    this.refreshProfile()
  },
  activated() {
    this.refreshProfile()
  },
  methods: {
    refreshProfile() {
      this.profile = { ...getStoredUserProfile() }
      this.avatarPreset = getProfileAvatarPreset()
    },
    onProfileAction(action) {
      if (this.loggingOut) return
      const fn = this[action]
      if (typeof fn === 'function') fn.call(this)
    },
    goProfileInfo() {
      if (this.loggingOut) return
      this.$router.push('/profile/info')
    },
    goChangePassword() {
      this.$router.push('/profile/change-password')
    },
    goUserAgreement() {
      this.$router.push('/user-agreement')
    },
    goPrivacyPolicy() {
      this.$router.push('/privacy-policy')
    },
    goWeekReport() {
      this.$router.push('/profile/week-report')
    },
    goTaskList() {
      this.$router.push('/profile/tasks')
    },
    async handleLogout() {
      if (this.loggingOut) return
      this.loggingOut = true
      this.$bus.$emit('logout-overlay', true)
      try {
        await logoutWithServer()
        this.profile = { ...getStoredUserProfile() }
        this.avatarPreset = getProfileAvatarPreset()
        await this.$router.replace('/login')
      } catch (error) {
        console.error('退出登录失败:', error)
        this.$message.error('退出失败，请重试')
        this.loggingOut = false
        this.$bus.$emit('logout-overlay', false)
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  background: #fff;
  padding: calc(clamp(22px, 5.5vw, 32px) + env(safe-area-inset-top, 0px)) clamp(14px, 4vw, 20px)
    calc(clamp(12px, 3vw, 16px) + var(--bottom-tab-height, 72px) + env(safe-area-inset-bottom, 0px));
}

.profile-page__inner {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  box-sizing: border-box;
}

.profile-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin-bottom: clamp(25px, 2.5vw, 29px);
  padding: clamp(4px, 1vw, 8px) 0 0;
  min-height: 64px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}

.profile-header:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.profile-header:active:not(:disabled) .profile-header__name {
  color: #4253ff;
}

.profile-header__main {
  display: flex;
  align-items: center;
  gap: clamp(12px, 3vw, 16px);
  min-width: 0;
  flex: 1;
}

.profile-header__avatar {
  flex-shrink: 0;
  width: clamp(64px, 18vw, 76px);
  height: clamp(64px, 18vw, 76px);
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #ffffff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: clamp(28px, 8vw, 34px);
  overflow: hidden;
}

.profile-header__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-header__info {
  min-width: 0;
  flex: 1;
}

.profile-header__name {
  margin: 0;
  font-size: clamp(18px, 4.8vw, 22px);
  font-weight: 700;
  color: #1e293b;
  line-height: 1.35;
  word-break: break-all;
}

.profile-header__tagline {
  margin: 6px 0 0;
  font-size: clamp(12px, 3.2vw, 14px);
  color: #94a3b8;
  line-height: 1.45;
}

.profile-header__guide {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  line-height: 1;
  pointer-events: none;
}

.profile-header__guide-text {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.02em;
}

.profile-header__guide .el-icon-arrow-right {
  font-size: 12px;
  color: #94a3b8;
}

.profile-summary-card {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: clamp(14px, 3.5vw, 18px);
  min-height: 120px;
  padding: 26px 18px;
  overflow: hidden;
  border: none;
  border-radius: clamp(14px, 3.5vw, 18px);
  background-color: #7eb6ea;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06), 0 1px 4px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  isolation: isolate;
}

.profile-summary-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.72) 0%,
    rgba(255, 255, 255, 0.48) 48%,
    rgba(255, 255, 255, 0.22) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.profile-summary-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.profile-summary-card:active:not(:disabled) {
  opacity: 0.92;
}

.profile-summary-card__body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.profile-summary-card__slogan {
  font-size: clamp(16px, 4.2vw, 18px);
  font-weight: 600;
  color: #0a1c44;
  line-height: 1.35;
  text-align: left;
}

.profile-summary-card__action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 500;
  color: #0a1c44;
  line-height: 1.3;
  text-align: left;
}

.profile-summary-card__action .el-icon-arrow-right {
  font-size: 12px;
  color: #0a1c44;
}

.profile-menu-card {
  background: #ffffff;
  border-radius: clamp(14px, 3.5vw, 18px);
  border: 1px solid #eef3ff;
  box-shadow: 0 2px 10px rgba(66, 83, 255, 0.08), 0 1px 4px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.profile-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: clamp(8px, 2.2vw, 12px);
  padding: clamp(10px, 2.6vw, 12px) clamp(14px, 3.8vw, 18px);
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: #334155;
  transition: background 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
}

.profile-menu-item--last {
  border-bottom: none;
}

.profile-menu-item:hover {
  background: #fafbfd;
}

.profile-menu-item:active {
  background: #f4f6fa;
}

.profile-menu-item__icon {
  flex-shrink: 0;
  width: clamp(32px, 8.5vw, 38px);
  height: clamp(32px, 8.5vw, 38px);
  border-radius: clamp(8px, 2vw, 10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(18px, 4.8vw, 20px);
}

.profile-menu-item__icon--blue {
  background: #eef2ff;
  color: #4253ff;
}

.profile-menu-item__icon--green {
  background: #ecfdf3;
  color: #22c55e;
}

.profile-menu-item__icon--orange {
  background: #fff7ed;
  color: #f59e0b;
}

.profile-menu-item__icon--purple {
  background: #f5f3ff;
  color: #7c5cff;
}

.profile-menu-item__icon--cyan {
  background: #eff6ff;
  color: #3b82f6;
}

.profile-menu-item__icon--indigo {
  background: #eef2ff;
  color: #4f46e5;
}

.profile-menu-item__label {
  flex: 1;
  min-width: 0;
  font-size: clamp(14px, 3.8vw, 16px);
  font-weight: 500;
  line-height: 1.35;
}

.profile-menu-item__chevron {
  flex-shrink: 0;
  font-size: 14px;
  color: #cbd5e1;
}

.profile-page--logging-out {
  pointer-events: none;
}

.profile-menu-item:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.profile-logout-btn:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.profile-logout-btn__loading {
  animation: profile-logout-spin 0.9s linear infinite;
}

@keyframes profile-logout-spin {
  to {
    transform: rotate(360deg);
  }
}

.profile-logout-btn {
  width: 100%;
  margin-top: clamp(10px, 2.5vw, 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: clamp(42px, 11vw, 48px);
  padding: 0 16px;
  border: 1px solid #ffd4d4;
  border-radius: clamp(14px, 3.5vw, 18px);
  background: #fff1f0;
  color: #ff4d4f;
  font-size: clamp(14px, 3.8vw, 16px);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.1);
  transition: background 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
}

.profile-logout-btn i {
  font-size: clamp(16px, 4.2vw, 18px);
}

.profile-logout-btn:hover {
  background: #ffe7e6;
}

.profile-logout-btn:active {
  opacity: 0.92;
}

@media (min-width: 769px) {
  .profile-page__inner {
    max-width: 480px;
  }
}
</style>
